import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { usePartnerSettings } from '@/hooks/usePartnerSettings';
import { usePartnerDocuments } from '@/hooks/usePartnerDocuments';
import { useDocumentExtraction, type ExtractedDocumentData } from '@/hooks/useDocumentExtraction';
import { useMultiTypeDetection, type DetectedPolicy } from '@/hooks/useMultiTypeDetection';
import { DocumentExtractionConfirmDialog } from './DocumentExtractionConfirmDialog';
import { MultiTypeDetectionDialog } from './MultiTypeDetectionDialog';
import { Upload, FileText, Sparkles, Loader2, Scan } from 'lucide-react';
import { toast } from 'sonner';

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  preselectedDocType?: string;
}

export function DocumentUploadDialog({ open, onOpenChange, companyId, preselectedDocType }: DocumentUploadDialogProps) {
  const { settings } = usePartnerSettings();
  const { uploadDocument, uploadMultiTypeDocument } = usePartnerDocuments(companyId);
  const { extractDocument, isExtracting } = useDocumentExtraction();
  const { detectDocumentTypes, isDetecting } = useMultiTypeDetection();
  
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState(preselectedDocType || '');
  const [documentNumber, setDocumentNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [issuingAuthority, setIssuingAuthority] = useState('');
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedDocumentData | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [detectedPolicies, setDetectedPolicies] = useState<DetectedPolicy[]>([]);
  const [showMultiTypeDialog, setShowMultiTypeDialog] = useState(false);
  const [useManualEntry, setUseManualEntry] = useState(false);

  const documentTypes = settings?.document_types || [];
  const selectedDocType = documentTypes.find(dt => dt.id === documentType);
  const showDateFields = selectedDocType?.has_expiry ?? true;

  // Reset document type when preselected type changes or dialog opens
  useEffect(() => {
    if (open) {
      setDocumentType(preselectedDocType || '');
    }
  }, [open, preselectedDocType]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      setUseManualEntry(false);
    }
  };

  const handleAnalyzeDocument = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    const result = await detectDocumentTypes(file, documentTypes);
    if (result && result.detected_policies.length > 0) {
      if (result.detected_policies.length === 1) {
        // Single policy: auto-fill the form
        const policy = result.detected_policies[0];
        setDocumentType(policy.document_type_id);
        if (policy.document_number) setDocumentNumber(policy.document_number);
        if (policy.issuing_authority) setIssuingAuthority(policy.issuing_authority);
        if (policy.issue_date) setIssueDate(policy.issue_date);
        if (policy.expiry_date) setExpiryDate(policy.expiry_date);
        setExtractedData({
          document_number: policy.document_number,
          issuing_authority: policy.issuing_authority,
          issue_date: policy.issue_date,
          expiry_date: policy.expiry_date,
          confidence: policy.confidence,
          holder_name: policy.holder_name,
          coverage_amount: policy.coverage_amount,
          additional_info: policy.additional_info,
        } as ExtractedDocumentData);
        setUseManualEntry(true);
      } else {
        // Multiple policies: show selection dialog
        setDetectedPolicies(result.detected_policies);
        setShowMultiTypeDialog(true);
      }
    } else {
      // Fallback to manual entry
      setUseManualEntry(true);
      toast.info('Please select the document type manually');
    }
  };

  const handleExtractFields = async () => {
    if (!file || !documentType) {
      toast.error('Please select a file and document type first');
      return;
    }

    const data = await extractDocument(file, documentType);
    if (data) {
      // Auto-populate form fields
      if (data.document_number) setDocumentNumber(data.document_number);
      if (data.issuing_authority) setIssuingAuthority(data.issuing_authority);
      if (data.issue_date) setIssueDate(data.issue_date);
      if (data.expiry_date) setExpiryDate(data.expiry_date);
      
      setExtractedData(data);
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmExtraction = async (confirmedData: ExtractedDocumentData) => {
    if (confirmedData.document_number) setDocumentNumber(confirmedData.document_number);
    if (confirmedData.issuing_authority) setIssuingAuthority(confirmedData.issuing_authority);
    if (confirmedData.issue_date) setIssueDate(confirmedData.issue_date);
    if (confirmedData.expiry_date) setExpiryDate(confirmedData.expiry_date);
    
    setShowConfirmDialog(false);
    await handleSubmitWithData(confirmedData);
  };

  const handleManualEntry = () => {
    setShowConfirmDialog(false);
    // Keep extracted data in form fields but don't auto-upload
  };

  const handleSubmitWithData = async (data?: ExtractedDocumentData) => {
    if (!file || !documentType) {
      toast.error('Please select a file and document type');
      return;
    }

    if (selectedDocType?.has_expiry && !expiryDate && !data?.expiry_date) {
      toast.error('Expiry date is required for this document type');
      return;
    }

    setUploading(true);
    try {
      await uploadDocument({
        companyId,
        data: {
          document_type: documentType,
          document_category: selectedDocType?.category || 'other',
          file,
          document_number: data?.document_number || documentNumber || undefined,
          issue_date: data?.issue_date || issueDate || undefined,
          expiry_date: data?.expiry_date || expiryDate || undefined,
          issuing_authority: data?.issuing_authority || issuingAuthority || undefined,
          is_required_for_prequalification: selectedDocType?.required_for_prequalification,
          extracted_data: data ? {
            ...data,
            coverage_type: data.coverage_type,
            coverage_amount: data.coverage_amount,
            holder_name: data.holder_name,
            additional_info: data.additional_info
          } : undefined,
          extraction_confidence: data?.confidence,
        },
      });
      
      // Reset form
      setFile(null);
      setDocumentType('');
      setDocumentNumber('');
      setIssueDate('');
      setExpiryDate('');
      setIssuingAuthority('');
      setExtractedData(null);
      onOpenChange(false);
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmitWithData(extractedData || undefined);
  };

  const handleConfirmMultiUpload = async (selectedPolicies: DetectedPolicy[]) => {
    if (!file) return;

    setUploading(true);
    try {
      await uploadMultiTypeDocument({
        file,
        policies: selectedPolicies.map(p => ({
          document_type_id: p.document_type_id,
          document_type_name: p.document_type_name,
          document_number: p.document_number,
          issue_date: p.issue_date,
          expiry_date: p.expiry_date,
          issuing_authority: p.issuing_authority,
          confidence: p.confidence,
          additional_info: p.additional_info,
          coverage_amount: p.coverage_amount,
          holder_name: p.holder_name,
        })),
      });

      // Reset form
      setFile(null);
      setDocumentType('');
      setDocumentNumber('');
      setIssueDate('');
      setExpiryDate('');
      setIssuingAuthority('');
      setExtractedData(null);
      setDetectedPolicies([]);
      setShowMultiTypeDialog(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="file">File *</Label>
              <div className="mt-2">
                <label 
                  htmlFor="file" 
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/50 hover:bg-muted transition-colors"
                >
                  {file ? (
                    <div className="flex items-center gap-2">
                      <FileText className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground">
                        PDF, Images, Word docs (max 10MB)
                      </p>
                    </div>
                  )}
                  <input
                    id="file"
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    disabled={uploading || isDetecting}
                  />
                </label>
              </div>
            </div>

            {file && !useManualEntry && !extractedData && (
              <Button
                type="button"
                variant="default"
                onClick={handleAnalyzeDocument}
                disabled={isDetecting || uploading}
                className="w-full"
              >
                {isDetecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analyzing document...
                  </>
                ) : (
                  <>
                    <Scan className="mr-2 h-4 w-4" />
                    Analyze Document for Multiple Policies
                  </>
                )}
              </Button>
            )}

            {(useManualEntry || extractedData) && (
              <>
                <div>
                  <Label htmlFor="documentType">Document Type *</Label>
                  <Select value={documentType} onValueChange={setDocumentType}>
                    <SelectTrigger id="documentType">
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map((dt) => (
                        <SelectItem key={dt.id} value={dt.id}>
                          {dt.name} {dt.required_for_prequalification && '(Required)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {file && documentType && !extractedData && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleExtractFields}
                    disabled={isExtracting || uploading}
                    className="w-full"
                  >
                    {isExtracting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing document...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Auto-Fill from Document
                      </>
                    )}
                  </Button>
                )}
              </>
            )}

            {(useManualEntry || extractedData) && (
              <>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="documentNumber">Document Number</Label>
                <Input
                  id="documentNumber"
                  value={documentNumber}
                  onChange={(e) => setDocumentNumber(e.target.value)}
                  placeholder="e.g., POL-123456"
                />
              </div>

              <div>
                <Label htmlFor="issuingAuthority">Issuing Authority</Label>
                <Input
                  id="issuingAuthority"
                  value={issuingAuthority}
                  onChange={(e) => setIssuingAuthority(e.target.value)}
                  placeholder="e.g., State Board"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="issueDate">Issue Date</Label>
                <Input
                  id="issueDate"
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="expiryDate">
                  Expiry Date {selectedDocType?.has_expiry && '*'}
                </Label>
                <Input
                  id="expiryDate"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  required={selectedDocType?.has_expiry}
                />
              </div>
            </div>

                {selectedDocType?.required_for_prequalification && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 text-sm">
                    <Checkbox checked disabled />
                    <span>Required for prequalification</span>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || isExtracting || isDetecting || !useManualEntry}>
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {extractedData && (
        <DocumentExtractionConfirmDialog
          open={showConfirmDialog}
          onOpenChange={setShowConfirmDialog}
          extractedData={extractedData}
          onConfirm={handleConfirmExtraction}
          onManualEntry={handleManualEntry}
          isSubmitting={uploading}
          showDateFields={showDateFields}
        />
      )}

      {detectedPolicies.length > 0 && (
        <MultiTypeDetectionDialog
          open={showMultiTypeDialog}
          onOpenChange={setShowMultiTypeDialog}
          detectedPolicies={detectedPolicies}
          onConfirm={handleConfirmMultiUpload}
          isSubmitting={uploading}
        />
      )}
    </>
  );
}
